import { useRef } from "react"
import { Button, Center, Drawer, Loader, Text, Group, CloseButton } from "@mantine/core"
import { Trans, useTranslation } from "react-i18next"
import { notifications } from "@mantine/notifications"
import { useUser } from "../../user/hooks/useUser"
import { modals } from "@mantine/modals"
import { useContact, useDeleteContact } from "../hooks/useContacts"
import ContactDetailContent from "./ContactDetailContent"
import ContactFormContent from "./ContactFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedContactId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function ContactDrawer({ opened, onClose, mode, selectedContactId, onDeleted, onSwitchToEdit }: Props) {
  const { t } = useTranslation()
  const { data: contact, isLoading } = useContact(mode !== "create" ? selectedContactId : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageContacts ?? false
  const deleteMutation = useDeleteContact()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t("contacts.confirmDelete"),
      children: <Text size="sm">{t("contacts.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(contact!.id, {
          onSuccess: () => {
            notifications.show({ color: "green", title: t("common.success"), message: t("contacts.deleteSuccess") })
            onDeleted()
          },
        })
      },
    })
  }

  const title =
    mode === "create"
      ? t("contacts.createTitle")
      : mode === "edit"
        ? t("contacts.editTitle")
        : t("contacts.title")

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md">{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <ContactFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !contact ? (
        <Text>
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <ContactFormContent ref={formRef} contact={contact} onSuccess={onClose} />
      ) : (
        <ContactDetailContent
          contact={contact}
        />
      )}
    </Drawer>
  )
}
